import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { UserCheck, Loader2, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/AuthContext";

export default function IntakeQLinkCard() {
  const { user } = useAuth();
  const isProvider = user?.role === "admin";
  const [link, setLink] = useState(null);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState("");
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!isProvider) { setLoading(false); return; }
    try {
      const [{ data: me }, { data: list }] = await Promise.all([
        API.get("/provider/me/intakeq-link"),
        API.get("/provider/intakeq-practitioners"),
      ]);
      setLink(me);
      setPractitioners(list || []);
      setSelected(me?.intakeq_practitioner_id || "");
    } catch {
      toast({ title: "Couldn't load IntakeQ practitioners", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [isProvider, toast]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/provider/me/intakeq-link", { intakeq_practitioner_id: selected || null });
      toast({ title: selected ? "Linked to IntakeQ practitioner" : "IntakeQ link cleared" });
      setEditing(false);
      load();
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!isProvider || loading) return null;

  const notLinked = !link?.linked;

  if (!editing) {
    return (
      <div className={`rounded-xl p-4 border ${notLinked ? "border-amber-300 bg-amber-50" : "bg-card border-border"}`} data-testid="intakeq-link-card">
        <button onClick={() => setEditing(true)} className="w-full flex items-center gap-3 text-left" data-testid="intakeq-link-open-button">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${notLinked ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-600"}`}>
            {notLinked ? <AlertCircle className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">IntakeQ Practitioner Link</p>
            <p className="text-xs text-muted-foreground truncate">
              {notLinked
                ? "Not linked — your patient list will be empty until you map yourself to your IntakeQ practitioner profile."
                : <>Linked to <span className="font-medium text-foreground">{link.name || link.intakeq_practitioner_id}</span></>
              }
            </p>
          </div>
          <span className="text-sm text-primary font-medium shrink-0">{notLinked ? "Link now" : "Change"}</span>
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="intakeq-link-form">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
          <UserCheck className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-sm">Link to your IntakeQ profile</p>
          <p className="text-xs text-muted-foreground">Select the IntakeQ practitioner that represents you. Your patient dashboard will then show everyone assigned to you.</p>
        </div>
      </div>

      <Select value={selected || "__none__"} onValueChange={(v) => setSelected(v === "__none__" ? "" : v)}>
        <SelectTrigger data-testid="intakeq-link-select">
          <SelectValue placeholder="Choose practitioner…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Not linked —</SelectItem>
          {practitioners.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`intakeq-link-option-${p.id}`}>
              <span className="flex items-center gap-2">
                {p.id === link?.intakeq_practitioner_id && <Check className="w-3 h-3 text-emerald-600" />}
                {p.name} {p.email && <span className="text-xs text-muted-foreground">· {p.email}</span>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1 rounded-full" onClick={() => { setEditing(false); setSelected(link?.intakeq_practitioner_id || ""); }} disabled={saving}>
          Cancel
        </Button>
        <Button className="flex-1 rounded-full" onClick={save} disabled={saving} data-testid="intakeq-link-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save link"}
        </Button>
      </div>
    </div>
  );
}
